import { InMemoryCache } from '@apollo/client';
import { CommentsQuery, GetMicrostoreSitesPaginatedQuery } from 'data/__generated__';
import { uniqBy } from 'lodash';

// to use union in fragments
export const possibleTypes = {
    OpsParameter: ['BooleanOpsParameter', 'NumericalOpsParameter'],
    RitualGame: ['QuizRitualGame', 'WantedRitualGame'],
    TourStop: ['Delivery', 'Task'],
    SetupInstruction: [
        'CheckTemperatureInstruction',
        'CheckBoxTemperatureInstruction',
        'HotDrinksInstruction',
        'InventoryInstruction',
        'ManualReassortInstruction',
        'MicrowaveInstruction',
        'MoveProductsInstruction',
        'PhotosInstruction',
        'RemoveProductsInstruction',
        'StoreProductsInstruction',
        'TheftNoticeInstruction',
    ],
    SupplyOrderProductIssue: [
        'BadExpiryDateIssue',
        'BrokenQuantityIssue',
        'DifferentPackagingIssue',
        'DifferentQuantityIssue',
        'NewBarcodeIssue',
        'NotReceivedIssue',
        'UnknownProductIssue',
        'WrongProductIssue',
    ],
    AuditLogPatch: ['AddPatch', 'ReplacePatch', 'RemovePatch', 'MovePatch', 'CopyPatch', 'TestPatch'],
};

const DEFAULT_KEY_ARGS = ['limit', 'sortBy', 'sortOrder', 'filters'];

export const cache = new InMemoryCache({
    possibleTypes,
    typePolicies: {
        BoxProduct: { keyFields: ['boxId', '_id'] },
        OrderProduct: { keyFields: ['orderId', '_id'] },
        ProductStockVariation: { keyFields: ['supplyOrderId', 'productId'] },
        SupplyOrderProduct: { keyFields: ['supplyOrderId', '_id'] },
        ControlPhoto: {
            keyFields: (photo) =>
                photo.taskInstructionId
                    ? ['taskInstructionId', 'type', 'photoIndex']
                    : ['deliveryId', 'setupInstructionId', 'type', 'photoIndex'],
        },
        User: {
            fields: {
                permissions: {
                    merge: false,
                },
                visitedSites: {
                    merge: false,
                },
            },
        },
        SupplyOrder: {
            fields: {
                products: {
                    merge: false,
                },
            },
        },
        Supplier: {
            fields: {
                productsInfoWithStock: {
                    merge: false,
                },
            },
        },
        Query: {
            fields: {
                credits: {
                    keyArgs: false,
                    merge(existing, incoming, { readField }) {
                        const existingItems = existing?.credits || [];
                        const incomingItems = incoming.credits;
                        const newCredits = [...incomingItems];
                        const newCreditIds = newCredits.map((credit) => readField('_id', credit));
                        existingItems.forEach((item: any) => {
                            if (!newCreditIds.includes(readField('_id', item))) {
                                newCredits.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            credits: newCredits,
                        };
                    },
                },
                catalogProducts: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField }) {
                        const existingItems = existing?.products || [];
                        const incomingItems = incoming.products;
                        const newProducts = [...incomingItems];
                        const newProductIds = newProducts.map((product) => readField('_id', product));
                        existingItems.forEach((item: any) => {
                            if (!newProductIds.includes(readField('_id', item))) {
                                newProducts.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            products: newProducts,
                        };
                    },
                },
                columnProducts: {
                    keyArgs: ['columnId', ...DEFAULT_KEY_ARGS],
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.columnProducts || [];
                        const incomingItems = incoming.columnProducts;

                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                columnProducts: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        const newColumnProducts = [...existingItems];
                        const newProductIds = newColumnProducts.map((cp) => readField('productId', cp));
                        incomingItems.forEach((item: any) => {
                            if (!newProductIds.includes(readField('productId', item))) {
                                newColumnProducts.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            columnProducts: newColumnProducts,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                siteProducts: {
                    keyArgs: ['siteId', ...DEFAULT_KEY_ARGS],
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.siteProducts || [];
                        const incomingItems = incoming.siteProducts;

                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                siteProducts: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        const newSiteProducts = [...existingItems];
                        const newProductIds = newSiteProducts.map((sp) => readField('productId', sp));
                        incomingItems.forEach((item: any) => {
                            if (!newProductIds.includes(readField('productId', item))) {
                                newSiteProducts.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            siteProducts: newSiteProducts,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                payments: {
                    keyArgs: ['stateFilter', 'organizationIdFilter'],
                    merge(existing, incoming, { readField }) {
                        const existingItems = existing?.payments || [];
                        const incomingItems = incoming.payments;
                        const newPayments = [...incomingItems];
                        const newPaymentIds = newPayments.map((payment) => readField('_id', payment));
                        existingItems.forEach((item: any) => {
                            if (!newPaymentIds.includes(readField('_id', item))) {
                                newPayments.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            payments: newPayments,
                        };
                    },
                },
                stockTransfers: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.stockTransfers || [];
                        const incomingItems = incoming.stockTransfers;

                        // A fresh query (offset 0, e.g. after a sort/filter change) replaces the list.
                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                stockTransfers: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        // fetchMore: append the next page below the already-loaded rows.
                        const newStockTransfers = [...existingItems];
                        const newStockTransferIds = newStockTransfers.map((stockTransfer) =>
                            readField('_id', stockTransfer),
                        );
                        incomingItems.forEach((item: any) => {
                            if (!newStockTransferIds.includes(readField('_id', item))) {
                                newStockTransfers.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            stockTransfers: newStockTransfers,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                supplyOrders: {
                    keyArgs: false,
                    merge: false,
                },
                paginatedFilteredSuppliers: {
                    keyArgs: ['filterString'],
                    merge(existing, incoming, { readField }) {
                        const existingItems = existing?.suppliers || [];
                        const incomingItems = incoming.suppliers;
                        const newSuppliers = [...incomingItems];
                        const newSuppliersIds = newSuppliers.map((supplier) => readField('_id', supplier));
                        existingItems.forEach((item: any) => {
                            if (!newSuppliersIds.includes(readField('_id', item))) {
                                newSuppliers.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            suppliers: newSuppliers,
                        };
                    },
                },
                transactions: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { args, readField }) {
                        const existingItems = existing?.transactions || [];
                        const incomingItems = incoming.transactions;

                        // Première page (chargement initial, ou refetch après un changement de filtre/tri) :
                        // on REMPLACE au lieu d'empiler. `PaginatedTransactions` n'expose pas d'offset, on lit
                        // donc celui des arguments. Sans ce reset, deux vues qui partagent la même entrée de
                        // cache (la section d'une fiche site et la page « voir plus », mêmes `keyArgs`)
                        // accumulent des pages qui ne se suivent pas.
                        if (!args?.offset) {
                            return { ...incoming, transactions: incomingItems, hasMore: incoming.hasMore };
                        }

                        const newTransactions = [...existingItems];
                        const newTransactionIds = newTransactions.map((transaction) => readField('_id', transaction));
                        incomingItems.forEach((item: any) => {
                            if (!newTransactionIds.includes(readField('_id', item))) {
                                newTransactions.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            transactions: newTransactions,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                sitesFilter: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField }) {
                        const existingItems = existing?.sites || [];
                        const incomingItems = incoming.sites;

                        if (incoming.offset === 0) {
                            return {
                                ...incoming,
                                sites: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        const newSites = [...existingItems];
                        const newSiteIds = newSites.map((site) => readField('_id', site));
                        incomingItems.forEach((item: any) => {
                            if (!newSiteIds.includes(readField('_id', item))) {
                                newSites.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            sites: newSites,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                microstoreColumnsFilter: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField }) {
                        const existingItems = existing?.microstoreColumns || [];
                        const incomingItems = incoming.microstoreColumns;

                        // If it's a new request (offset: 0), we replace completely
                        if (incoming.offset === 0) {
                            return {
                                ...incoming,
                                microstoreColumns: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        // Otherwise, we merge the results
                        const newColumns = [...existingItems];
                        const newColumnIds = newColumns.map((column) => readField('_id', column));
                        incomingItems.forEach((item: any) => {
                            if (!newColumnIds.includes(readField('_id', item))) {
                                newColumns.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            microstoreColumns: newColumns,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                microstoreSitesWithColumnGroupsFilter: {
                    keyArgs: ['filterString'],
                    merge(
                        existing: GetMicrostoreSitesPaginatedQuery['microstoreSitesWithColumnGroupsFilter'] | undefined,
                        incoming: GetMicrostoreSitesPaginatedQuery['microstoreSitesWithColumnGroupsFilter'],
                        { readField },
                    ) {
                        const existingItems = existing?.sites || [];
                        const incomingItems = incoming.sites;
                        const newSites = [...existingItems];
                        const newSiteIds = newSites.map((site) => readField('_id', site));
                        incomingItems.forEach((item) => {
                            if (!newSiteIds.includes(readField('_id', item))) {
                                newSites.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            sites: newSites,
                        };
                    },
                },
                users: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.users || [];
                        const incomingItems = incoming.users;

                        if ((args?.offset ?? 0) === 0) {
                            return { ...incoming, users: incomingItems, hasMore: incoming.hasMore };
                        }

                        const newUsers = [...existingItems];
                        const newUserIds = newUsers.map((user) => readField('_id', user));
                        incomingItems.forEach((item: any) => {
                            if (!newUserIds.includes(readField('_id', item))) {
                                newUsers.push(item);
                            }
                        });

                        return { ...incoming, users: newUsers, hasMore: incoming.hasMore };
                    },
                },
                bundlesFilter: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.bundles || [];
                        const incomingItems = incoming.bundles;

                        if ((args?.offset ?? 0) === 0) {
                            return { ...incoming, bundles: incomingItems, hasMore: incoming.hasMore };
                        }

                        const newBundles = [...existingItems];
                        const newBundleIds = newBundles.map((bundle) => readField('_id', bundle));
                        incomingItems.forEach((item: any) => {
                            if (!newBundleIds.includes(readField('_id', item))) {
                                newBundles.push(item);
                            }
                        });

                        return { ...incoming, bundles: newBundles, hasMore: incoming.hasMore };
                    },
                },
                comments: {
                    keyArgs: false,
                    merge(
                        existing: CommentsQuery['comments'] | undefined,
                        incoming: CommentsQuery['comments'],
                        { readField },
                    ) {
                        const existingComments = existing?.comments ?? [];
                        const mergedComments = uniqBy([...existingComments, ...incoming.comments], (comment) =>
                            readField('_id', comment),
                        );
                        return { ...incoming, comments: mergedComments };
                    },
                },
            },
        },
    },
});
